// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Lock, Mail, Eye, EyeOff, Loader2, Sparkles, ShieldCheck, Dog } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/');
    
    // Cargar Branding Público
    axios.get('/backend/api/configuracion/branding.php')
      .then(res => {
        if (res.data.success) {
          setLogoUrl(res.data.data.logo_url);
        }
      })
      .catch(() => setLogoUrl(null));
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await login(email, password);
    if (!result.success) {
      setError(result.message);
    } else {
      navigate('/');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen w-full flex bg-[#020617] overflow-hidden">
      
      {/* 🔹 LEFT PANEL: BRANDING & ATMOSPHERE */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden group">
        <div 
          className="absolute inset-0 bg-cover bg-center scale-105 group-hover:scale-100 transition-transform duration-[10s] ease-out"
          style={{ backgroundImage: 'url("/backend/assets/branding/login_bg.png")' }}
        ></div>
        
        {/* Gradients & Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-transparent to-[#020617]/40 opacity-90"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent opacity-60"></div>

        {/* Branding Content */}
        <div className="relative z-10 w-full h-full p-16 flex flex-col justify-between">
          <div className="page-enter flex flex-col gap-10">
             {/* 🚀 LOGO DE LA APLICACIÓN (EL MOTOR / SOFTWARE) */}
             <div className="inline-block p-10 glass rounded-[3.5rem] border-white/10 shadow-[0_40px_80px_-15px_rgba(0,0,0,0.8)] backdrop-blur-3xl w-fit group">
                <img 
                  src="/backend/assets/branding/logo_pro_giant.png" 
                  alt="Application Logo" 
                  className="h-32 w-auto object-contain drop-shadow-[0_0_30px_rgba(139,92,246,0.4)] group-hover:scale-110 transition-transform duration-700" 
                />
             </div>

             {/* 🏢 LOGO DEL NEGOCIO / EMPRESA (EL CLIENTE) */}
             <div className="flex items-center gap-5 bg-slate-900/40 border border-white/5 rounded-3xl p-5 backdrop-blur-2xl w-fit animate-in slide-in-from-left duration-1000 shadow-2xl">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-primary/20 to-secondary/20 p-2 shadow-inner border border-white/5">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Business Instance" className="w-full h-full object-contain" />
                  ) : (
                    <Dog className="text-white/20 w-full h-full" />
                  )}
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase tracking-[4px] text-white/10 italic">Instancia Corporativa:</p>
                  <p className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-white to-white/40 tracking-tighter uppercase leading-none mt-1">
                    Terminal Operativa
                  </p>
                </div>
             </div>
          </div>

          <div className="max-w-xl page-enter">
            <h2 className="text-sm font-bold tracking-[8px] uppercase text-primary-light/60 mb-4 flex items-center gap-3">
              <span className="w-10 h-[1px] bg-primary-light/40"></span> Business Analytics Suite
            </h2>
            <h1 className="text-6xl font-black text-white leading-tight mb-6">
              PAWSPA <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light via-cyan-400 to-white">PRO</span>
            </h1>
            <p className="text-xl text-white/50 font-medium leading-relaxed mb-10 max-w-lg">
              Gestión profesional de alta gama diseñada para el cuidado premium de mascotas en Puerto Rico.
            </p>

            {/* Micro Modules Display */}
            <div className="grid grid-cols-2 gap-4">
              <div className="glass p-4 rounded-2xl flex items-center gap-3 border-white/5">
                <div className="w-2 h-2 rounded-full bg-primary-light animate-pulse shadow-[0_0_10px_#8b5cf6]"></div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-white/70">Agenda Operativa</span>
              </div>
              <div className="glass p-4 rounded-2xl flex items-center gap-3 border-white/5">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_10px_#06b6d4]"></div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-white/70">Facturación & IVU</span>
              </div>
              <div className="glass p-4 rounded-2xl flex items-center gap-3 border-white/5">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shadow-[0_0_10px_#fbbf24]"></div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-white/70">Análisis Financiero</span>
              </div>
              <div className="glass p-4 rounded-2xl flex items-center gap-3 border-white/5">
                <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_#10b981]"></div>
                <span className="text-[10px] uppercase font-bold tracking-widest text-white/70">Reportes SURI</span>
              </div>
            </div>
          </div>

          <div className="text-white/20 text-[10px] font-bold tracking-[4px] uppercase flex items-center gap-3">
            <ShieldCheck size={14} /> Encrypted Enterprise Environment — Cit Auto Tech © 2026
          </div>
        </div>
      </div>

      {/* 🔹 RIGHT PANEL: LOGIN FORM */}
      <div className="w-full lg:w-2/5 flex items-center justify-center p-8 lg:p-16 relative bg-[#020617]">
        <div className="w-full max-w-md">
          <div className="text-center lg:text-left mb-12">
            <h1 className="text-3xl font-black text-white mb-2 uppercase tracking-tight">Entrar al Sistema</h1>
            <p className="text-white/40 font-medium">Bienvenido de nuevo, agente de servicio.</p>
            <div className="w-16 h-1.5 bg-gradient-to-r from-primary to-cyan-500 rounded-full mt-6"></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-[3px] text-white/40 ml-1">Email Corporativo</label>
              <div className="relative group">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary transition-colors" size={20} />
                <input
                  type="email"
                  placeholder="name@pawspro.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-16 bg-white/[0.03] text-white pl-14 pr-6 rounded-2xl outline-none border border-white/5 focus:border-primary/50 focus:bg-white/[0.05] transition-all font-medium text-sm placeholder:text-white/10"
                  required
                />
              </div>
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-bold uppercase tracking-[3px] text-white/40 ml-1">Pin / Código de Acceso</label>
              <div className="relative group">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-primary transition-colors" size={20} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-16 bg-white/[0.03] text-white pl-14 pr-14 rounded-2xl outline-none border border-white/5 focus:border-primary/50 focus:bg-white/[0.05] transition-all font-medium text-sm placeholder:text-white/10"
                  required
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-5 top-1/2 -translate-y-1/2 text-white/20 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="flex justify-end pr-1">
                <a href="/reset" onClick={(e) => e.preventDefault()} className="text-[10px] font-bold text-primary-light/40 hover:text-primary-light transition-colors uppercase tracking-[1px]">¿Olvidaste tu código?</a>
              </div>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/10 text-red-500 p-5 rounded-2xl text-xs font-bold leading-relaxed flex items-center gap-4 animate-shake">
                <div className="min-w-[6px] h-6 rounded-full bg-red-500"></div>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-16 bg-gradient-to-r from-primary to-[#7c3aed] text-white rounded-2xl font-black text-sm uppercase tracking-[4px] shadow-[0_15px_30px_-10px_rgba(139,92,246,0.6)] hover:shadow-[0_20px_40px_-10px_rgba(139,92,246,0.8)] hover:-translate-y-1 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-4 group disabled:opacity-50 disabled:translate-y-0"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span>Iniciar Sesión</span>
                  <Sparkles size={18} className="group-hover:rotate-45 transition-transform" />
                </>
              )}
            </button>
          </form>
          <div className="mt-16 text-center text-white/10 text-[9px] font-bold tracking-[5px] uppercase">
             PawSpa Management Engine — Authorized Personnel Only
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
